import React from 'react';
// eslint-disable-next-line import/no-named-default
import type { UrlObject } from 'url';
import NextLink, { LinkProps } from 'next/link';
import _ from 'lodash';
import Url from 'url-parse';
import Router from 'next/router';

export const PERSISTED_QUERY_PARAMS = ['redirect_to', 'spoof', 'forceLoadAuditAutosave', 'project'];

export const addPersistedQueryParamsToUrl = (url: string) => {
  return addQueryParamsToUrl(url, getPersistedQueryParams());
};

export const getPersistedQueryParams = () => {
  if (typeof Url === 'undefined' || typeof window === 'undefined') return {};

  const parsedUrl = new Url(window.location.href, true);
  return _.pick(parsedUrl.query, PERSISTED_QUERY_PARAMS);
};

if (typeof window !== 'undefined') {
  // @ts-ignore
  window.Url = Url;
}

const addQueryParamsToUrl = (url: any, queryParams: Record<string, any>) => {
  if (!url) return url;
  if (typeof url === 'string') {
    const urlObject = new Url(url, true);
    _.forEach(queryParams, (value, key) => {
      urlObject.query[key] = value;
    });
    return urlObject.toString();
  } else if (typeof url === 'object') {
    // support url objects -- https://github.com/zeit/next.js/#with-url-object
    return {
      ...url,
      query: {
        ...url.query,
        ...queryParams,
      },
    };
  } else {
    throw new Error(`Invalid object: ${url}`);
  }
};

// like next/Link but rewrites links to preserve spoof on them
export default class Link extends React.Component<LinkProps, { queryParams?: any }> {
  constructor(props: LinkProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    const queryParams = getPersistedQueryParams();
    if (queryParams) {
      this.setState({
        queryParams,
      });
    }
  }

  render() {
    if (!_.isEmpty(this.state.queryParams)) {
      return (
        <NextLink
          {...this.props}
          href={addQueryParamsToUrl(this.props.href, this.state.queryParams)}
          as={addQueryParamsToUrl(this.props.as, this.state.queryParams)}
          legacyBehavior
        />
      );
    }
    return <NextLink {...this.props} legacyBehavior />;
  }
}

// like Router.push but rewrites links to preserve spoof on them
export const push = (href: UrlObject | string, as?: UrlObject | string, options?: any) => {
  const qs = getPersistedQueryParams();
  if (!_.isEmpty(qs)) {
    href = addQueryParamsToUrl(href, qs);
    as = addQueryParamsToUrl(as, qs);
  }
  return Router.push(href, as, options);
};

// like Router.replace but rewrites links to preserve spoof on them
export const replace = (
  href: UrlObject | string,
  as?: UrlObject | string,
  options?: any,
  forceReloadPage = false,
) => {
  const qs = getPersistedQueryParams();
  if (!_.isEmpty(qs)) {
    href = addQueryParamsToUrl(href, qs);
    as = addQueryParamsToUrl(as, qs);
  }
  if (forceReloadPage) {
    const replacePromise = Router.replace(href, as, options);
    // eslint-disable-next-line promise/catch-or-return
    replacePromise.then(() => Router.reload());
    return replacePromise;
  }
  return Router.replace(href, as, options);
};
